import { useQueryClient } from '@tanstack/react-query'
import cn from 'clsx'
import { ChangeEvent, FC, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Typography } from '@/components/ui'

import { TasksService } from '@/services/tasks.service'

import UploadCloud from '@/assets/icons/upload-cloud.svg'

import {
	IUploadFile,
	useCreateShotlistDrafts
} from '@/features/Home/hooks/useCreateShotlistDrafts'
import { useHome } from '@/features/Home/hooks/useHome'

import { useRestoreShotlistUploadModal } from '../../hooks/useRestoreShotlistUploadModal'

import { DraftsContext } from './DraftsContext'
import UploadFileItem from './components/UploadFileItem'

const ShotlistUpload: FC = () => {
	const [files, setFiles] = useState<IUploadFile[]>([])
	const [isDragOver, setIsDragOver] = useState(false)
	const [activeUploading, setActiveUploading] = useState<string | null>(null)
	const [activeModal, setActiveModal] = useState<string | null>(null)

	const { drafts, removeDraft } = useContext(DraftsContext)
	const { tasks } = useHome()
	const { createDrafts } = useCreateShotlistDrafts({ setFiles })
	const location = useLocation()

	const inputRef = useRef<HTMLInputElement>(null)
	const dragCounter = useRef(0)

	const queryClient = useQueryClient()
	const state = location.state as { openFormModal?: string } | null
	const openFormModalId =
		state?.openFormModal || sessionStorage.getItem('openFormModal')

	useRestoreShotlistUploadModal({
		openFormModalId,
		tasks,
		locationState: location.state,
		setActiveModal,
		setActiveUploading
	})

	// Cleaning drafts without controllers, when user left the route (page) and then returned again
	useEffect(() => {
		if (drafts.length === 0 || openFormModalId) return

		drafts.forEach(async draft => {
			if (!draft.controller && !draft.draft.has_video) {
				removeDraft(draft.id)
				await TasksService.deleteTask(draft.id)
				queryClient.invalidateQueries({ queryKey: ['getTasks'] })
			}
		})
	}, [])

	const hasDraggedFiles = (e: React.DragEvent) =>
		Array.from(e.dataTransfer.types || []).includes('Files')

	const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(e.target.files || [])
		if (!selectedFiles.length) return

		await createDrafts(selectedFiles)

		e.target.value = ''
	}

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
	}

	const handleDragIn = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (!hasDraggedFiles(e)) return

		dragCounter.current++
		if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
			setIsDragOver(true)
		}
	}

	const handleDragOut = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (!hasDraggedFiles(e)) return

		dragCounter.current--
		if (dragCounter.current === 0) {
			setIsDragOver(false)
		}
	}

	const handleDrop = async (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(false)
		dragCounter.current = 0

		const droppedFiles = Array.from(e.dataTransfer.files || [])
		if (!droppedFiles.length) return

		await createDrafts(droppedFiles)
	}

	// Draft and file deleting from context and state respectively
	const clearItem = (id: string) => {
		removeDraft(id)
		setFiles(prev => prev.filter(item => item.taskId !== id))

		if (inputRef.current) inputRef.current.value = ''
	}

	return (
		<div className={cn('relative h-screen w-[25vw]')}>
			{isDragOver && (
				<div
					className={cn(
						'fixed inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-xl'
					)}
					onDrop={handleDrop}
					onDragOver={handleDrag}
					onDragEnter={handleDragIn}
					onDragLeave={handleDragOut}
				>
					<Typography
						variant='text-xlarge'
						className='text-center text-gray-600'
					>
						<UploadCloud className='mx-auto mb-4' />
						Drop your files here
					</Typography>
				</div>
			)}
			<div
				className={cn(
					'fixed w-[25vw] h-full z-[40] flex flex-col items-center justify-center transition-colors duration-200 bg-gray-50',
					drafts?.length > 0 && '!justify-start !pt-[50px] px-5',
					activeModal && '!z-0'
				)}
				onDrop={handleDrop}
				onDragOver={handleDrag}
				onDragEnter={handleDragIn}
				onDragLeave={handleDragOut}
			>
				<Typography
					variant='text-xlarge'
					className={cn(
						'w-full !text-[16px] !leading-[24px] !font-[500] text-center text-gray-600 py-4 rounded-[16px]',
						drafts?.length > 0 && 'border-2 border-dashed border-blue-700/10'
					)}
				>
					<UploadCloud className='mx-auto mb-[10px]' />
					Drag and drop your files here
					<br />
					or{' '}
					<label
						htmlFor='file-upload'
						className='relative text-blue-700 cursor-pointer'
					>
						browse files
					</label>
					<input
						id='file-upload'
						type='file'
						name='file'
						multiple
						accept='.mp4,.mov,.mp3,video/mp4,video/quicktime,audio/mpeg'
						ref={inputRef}
						className='hidden'
						onChange={handleFileChange}
					/>
				</Typography>
				{drafts.length > 0 && (
					<div className='fixed top-0 w-[25vw] h-[85vh] mt-[160px] px-5 pt-5 overflow-y-auto'>
						{drafts.map(draft => {
							const uploadFile = files.find(f => f.taskId === draft.id)?.file

							return (
								<UploadFileItem
									key={draft.id}
									draftState={draft}
									file={uploadFile}
									clearItem={clearItem}
									activeUploading={activeUploading}
									setActiveUploading={setActiveUploading}
									isModalOpen={activeModal === draft.id}
									setIsModalOpen={isOpen =>
										setActiveModal(isOpen ? draft.id : null)
									}
								/>
							)
						})}
					</div>
				)}
			</div>
		</div>
	)
}

export default ShotlistUpload
